import React, { useState, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Search, MapPin, Loader } from 'lucide-react';
import { noAutofillProps } from '../../utils/noAutofill';

const RestaurantAutocomplete = ({
  value,
  onChange,
  placeholder,
  className,
  onPlaceSelect
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const searchRestaurants = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${getApiBase()}/places/autocomplete`, {
        params: {
          query: query,
          types: 'establishment' // This will include restaurants and other food establishments
        },
        headers: getAuthHeaders()
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        // Filter for restaurant-related establishments
        const restaurantPredictions = response.data.predictions.filter(prediction => {
          const types = prediction.types || [];
          const description = (prediction.description || '').toLowerCase();

          // Restaurant-related types from Google Places
          const restaurantTypes = [
            'restaurant', 'food', 'meal_takeaway', 'meal_delivery',
            'cafe', 'bakery', 'bar', 'night_club'
          ];

          // Restaurant keywords
          const restaurantKeywords = [
            'restaurant', 'cafe', 'bistro', 'diner', 'grill', 'pizza',
            'bar', 'kitchen', 'eatery', 'food', 'pizzeria', 'tavern',
            'pub', 'steakhouse', 'brewery', 'bakery', 'sushi', 'taco',
            'burger', 'bbq', 'seafood', 'buffet', 'dining'
          ];

          const hasRestaurantType = types.some(type =>
            restaurantTypes.includes(type.toLowerCase())
          );
          const hasRestaurantKeyword = restaurantKeywords.some(keyword =>
            description.includes(keyword)
          );

          return hasRestaurantType || hasRestaurantKeyword;
        });

        setSuggestions(restaurantPredictions.slice(0, 8));
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
      } else {
        console.warn('Google Places API returned status:', response.data.status);
        setError('Unable to fetch restaurant suggestions');
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching restaurant suggestions:', err);
      if (err.response?.status === 503) {
        setError('Google Places API is not configured. Contact your administrator.');
        console.error('Google Places API key not configured. See QUICKSTART_FIX.md for setup instructions.');
      } else {
        setError('Failed to fetch restaurant suggestions');
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchRestaurants(inputValue);
    }, 300);
  };

  const handleSelectRestaurant = async (prediction) => {
    const displayValue = prediction.structured_formatting?.main_text || prediction.description;
    onChange(displayValue);
    setShowSuggestions(false);
    setSuggestions([]);

    // If onPlaceSelect is provided, fetch detailed place information
    if (onPlaceSelect) {
      try {
        const response = await axios.get(`${getApiBase()}/places/details`, {
          params: {
            place_id: prediction.place_id
          },
          headers: getAuthHeaders()
        });

        if (response.data.status === 'OK' && response.data.result) {
          const place = response.data.result;
          onPlaceSelect({
            place_id: place.place_id,
            name: place.name,
            formatted_address: place.formatted_address,
            geometry: place.geometry,
            types: place.types || [],
            rating: place.rating,
            price_level: place.price_level,
            phone: place.formatted_phone_number,
            website: place.website
          });
        }
      } catch (err) {
        console.error('Error fetching place details:', err);
        // Still call onPlaceSelect with basic info from prediction
        onPlaceSelect({
          place_id: prediction.place_id,
          name: prediction.structured_formatting?.main_text || '',
          formatted_address: prediction.description,
          types: prediction.types || []
        });
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    if (value && value.length >= 2) {
      searchRestaurants(value);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ui-muted w-4 h-4" />
        <input
          ref={inputRef}
          type="text"
          {...noAutofillProps}
          name="restaurant-search"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder || "Search for restaurants..."}
          className="w-full pl-10 pr-4 py-2 border border-ui-control rounded-lg focus:ring-2 focus:ring-ui-orange-solid focus:border-transparent"
        />
        {loading && (
          <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ui-muted w-4 h-4 animate-spin" />
        )}
      </div>

      {error && !loading && (
        <div className="absolute z-10 w-full bg-ui-amber-bg border border-ui-amber-line rounded-md shadow-lg mt-1 p-2">
          <div className="text-xs text-ui-amber-fg">
            ⚠️ {error}
          </div>
        </div>
      )}

      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-ui-surface border border-ui-control rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              🔍 Searching restaurants...
            </div>
          )}

          {!loading && suggestions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectRestaurant(prediction)}
              className="w-full text-left px-4 py-3 hover:bg-ui-hover focus:bg-ui-sunk focus:outline-none border-b border-ui-divider last:border-b-0"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-ui-muted mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ui-ink truncate">
                    {prediction.structured_formatting?.main_text}
                  </div>
                  {prediction.structured_formatting?.secondary_text && (
                    <div className="text-sm text-ui-muted truncate">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}

          {!loading && suggestions.length === 0 && value && value.length >= 2 && !error && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              No restaurants found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RestaurantAutocomplete;
